import { OrderStatus } from '../types/Order';
import { Team } from '../types/Team';
import { User } from '../types/User';

type BaseNotification = {
    id: string;
    from?: User['id'];
    fromCompany?: string | null;
    to: User['id'];
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    readAt?: string;
    handledAt?: string;
    team: Team['id'];
    cache?: {
        app?: object;
    };
};

type DiagramCommentNotificationBaseData = {
    commentId: string;
    content: string;
    attachment?: boolean;
};

export enum NotificationKey {
    DIAGRAM_COMMENT_CREATE = 'diagram.comment.create',
    DIAGRAM_COMMENT_REPLY_USER = 'diagram.comment.reply.user',
    DIAGRAM_COMMENT_REPLY_AI = 'diagram.comment.reply.ai',
    DIAGRAM_COMMENT_MENTION_USER = 'diagram.comment.mention.user',
    DIAGRAM_COMMENT_MENTION_MANUFACTURER = 'diagram.comment.mention.manufacturer',
    DIAGRAM_INVITE = 'diagram.invite',
    DIAGRAM_REQUESTACCESS = 'diagram.requestAccess',
    DESIGN_DUPLICATE = 'design.duplicate',
    ORDER_EVENT = 'order.event',
    ORDER_MESSAGE = 'order.message',
    DIAGRAM_CHAT_MESSAGE = 'diagram.chat.message',
    DIAGRAM_CHAT_CHANNEL = 'diagram.chat.channel',
    DIAGRAM_CHAT_CREDITS_ESTIMATE = 'diagram.chat.credits.estimate',
    DIAGRAM_CHAT_CREDITS_ACCEPT = 'diagram.chat.credits.accept',
    DIAGRAM_CHAT_CREDITS_DECLINE = 'diagram.chat.credits.decline',
    DIAGRAM_CHAT_CREDITS_CHARGE = 'diagram.chat.credits.charge',
    DIAGRAM_CHAT_REACTION = 'diagram.chat.reaction',
    INTERCOM_MESSAGE = 'intercom.message',
    INTERCOM_REACTION = 'intercom.reaction',
    COMPANY_REQUEST_ACCESS = 'company.requestAccess',
    COMPANY_GRANT_ACCESS = 'company.grantAccess',
    COMPANY_DENY_ACCESS = 'company.denyAccess',
    TEAM_REQUEST_ACCESS = 'team.requestAccess',
    TEAM_GRANT_ACCESS = 'team.grantAccess',
    TEAM_DENY_ACCESS = 'team.denyAccess',
    USER_TEAM_INVITE = 'user.teamInvite',
    COMPANY_ADDED_AS_PARTNER = 'company.addedAsPartner',
    COMPANY_NEW_EXHIBITOR_MATCH_LEAD = 'company.newExhibitorMatchLead',
    COMPANY_NEW_QR_SCAN = 'company.newQrScan',
}

type NotificationDiagramCommentCreate = BaseNotification & {
    type: NotificationKey.DIAGRAM_COMMENT_CREATE;
    data: DiagramCommentNotificationBaseData;
};

type NotificationDiagramCommentReplyUser = BaseNotification & {
    type: NotificationKey.DIAGRAM_COMMENT_REPLY_USER;
    data: DiagramCommentNotificationBaseData;
};

type NotificationDiagramCommentReplyAI = BaseNotification & {
    type: NotificationKey.DIAGRAM_COMMENT_REPLY_AI;
    data: DiagramCommentNotificationBaseData;
};

type NotificationDiagramCommentMentionUser = BaseNotification & {
    type: NotificationKey.DIAGRAM_COMMENT_MENTION_USER;
    data: DiagramCommentNotificationBaseData & {
        mention: string;
    };
};

export type NotificationDiagramCommentMentionManufacturer = BaseNotification & {
    type: NotificationKey.DIAGRAM_COMMENT_MENTION_MANUFACTURER;
    data: DiagramCommentNotificationBaseData & {
        mention: string;
        manufacturerName: string;
    };
};

export type NotificationDiagramInvite = BaseNotification & {
    type: NotificationKey.DIAGRAM_INVITE;
    data: {
        projectId: string;
        login: {
            url: string;
            code: string;
        };
        isNewUser: boolean;
    };
};

export type NotificationDiagramRequestAccess = BaseNotification & {
    type: NotificationKey.DIAGRAM_REQUESTACCESS;
    data: {
        projectId: string;
        userId: string;
    };
};

export type NotificationDesignDuplicate = BaseNotification & {
    type: NotificationKey.DESIGN_DUPLICATE;
    data: {
        referenceDesignId: string;
        designId: string;
    };
};

export type NotificationDiagramComment =
    | NotificationDiagramCommentCreate
    | NotificationDiagramCommentReplyUser
    | NotificationDiagramCommentReplyAI
    | NotificationDiagramCommentMentionUser
    | NotificationDiagramCommentMentionManufacturer;

type NotificationOrderEvent = BaseNotification & {
    type: NotificationKey.ORDER_EVENT;
    data: {
        orderId: string;
        status: OrderStatus;
    };
};

type NotificationOrderMessage = BaseNotification & {
    type: NotificationKey.ORDER_MESSAGE;
    data: {
        orderId: string;
    };
};

type NotificationDiagramChatChannel = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_CHANNEL;
    data: {
        channelId: string;
        messageId?: string;
    };
};

type NotificationDiagramChatMessage = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_MESSAGE;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationDiagramChatCreditsEstimate = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_CREDITS_ESTIMATE;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationDiagramChatCreditsAccept = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_CREDITS_ACCEPT;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationDiagramChatCreditsDecline = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_CREDITS_DECLINE;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationDiagramChatCreditsCharge = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_CREDITS_CHARGE;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationDiagramChatReaction = BaseNotification & {
    type: NotificationKey.DIAGRAM_CHAT_REACTION;
    data: {
        channelId: string;
        messageId: string;
        reaction: string;
    };
};

type NotificationIntercomMessage = BaseNotification & {
    type: NotificationKey.INTERCOM_MESSAGE;
    data: {
        channelId: string;
        messageId: string;
    };
};

type NotificationIntercomReaction = BaseNotification & {
    type: NotificationKey.INTERCOM_REACTION;
    data: {
        channelId: string;
        messageId: string;
        reaction: string;
    };
};

export type NotificationCompanyRequestAccess = BaseNotification & {
    type: NotificationKey.COMPANY_REQUEST_ACCESS;
    data: {
        companyId: string;
        teamId: string;
        userId: string;
        userTeamId: string;
    };
};

export type NotificationCompanyGrantAccess = BaseNotification & {
    type: NotificationKey.COMPANY_GRANT_ACCESS;
    data: {
        companyId: string;
        teamId: string;
        userId: string;
        redirect?: string;
    };
};

export type NotificationCompanyAddedAsPartner = BaseNotification & {
    type: NotificationKey.COMPANY_ADDED_AS_PARTNER;
    data: {
        addedByCompanyId: string;
        companyId: string;
    };
};

export type NotificationCompanyNewExhibitorMatchLead = BaseNotification & {
    type: NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD;
    data: {
        exhibitorMatchLeadId: string;
        companyId: string;
    };
};

export type NotificationCompanyNewQrScan = BaseNotification & {
    type: NotificationKey.COMPANY_NEW_QR_SCAN;
    data: {
        qrScanId: string;
        companyId: string;
    };
};

export type NotificationCompanyDenyAccess = BaseNotification & {
    type: NotificationKey.COMPANY_DENY_ACCESS;
    data: {
        companyId: string;
        userId: string;
        message: string;
    };
};

export type NotificationTeamRequestAccess = BaseNotification & {
    type: NotificationKey.TEAM_REQUEST_ACCESS;
    data: {
        teamId: string;
        userId: string;
    };
};

export type NotificationTeamGrantAccess = BaseNotification & {
    type: NotificationKey.TEAM_GRANT_ACCESS;
    data: {
        teamId: string;
        userId: string;
    };
};

export type NotificationTeamDenyAccess = BaseNotification & {
    type: NotificationKey.TEAM_DENY_ACCESS;
    data: {
        teamId: string;
        userId: string;
        message: string;
    };
};

export type NotificationUserTeamInvite = BaseNotification & {
    type: NotificationKey.USER_TEAM_INVITE;
    data: {
        teamId: string;
        userId: string;
        login: {
            url: string;
            code: string;
        };
        isNewUser: boolean;
    };
};

export type NotificationOrder = NotificationOrderEvent | NotificationOrderMessage;

export type Notification =
    | NotificationDiagramComment
    | NotificationDiagramInvite
    | NotificationDiagramRequestAccess
    | NotificationDesignDuplicate
    | NotificationDiagramChatChannel
    | NotificationDiagramChatMessage
    | NotificationDiagramChatCreditsEstimate
    | NotificationDiagramChatCreditsAccept
    | NotificationDiagramChatCreditsDecline
    | NotificationDiagramChatCreditsCharge
    | NotificationDiagramChatReaction
    | NotificationOrder
    | NotificationIntercomMessage
    | NotificationIntercomReaction
    | NotificationCompanyRequestAccess
    | NotificationCompanyGrantAccess
    | NotificationCompanyAddedAsPartner
    | NotificationCompanyNewExhibitorMatchLead
    | NotificationCompanyNewQrScan
    | NotificationCompanyDenyAccess
    | NotificationUserTeamInvite
    | NotificationTeamRequestAccess
    | NotificationTeamGrantAccess
    | NotificationTeamDenyAccess;

export type InboxNotification = Notification & {
    rank: number;
    app: {
        url: string;
        text: string;
        preview?: string;
        image?: string;
        conversation?: { sender: string; location: string[] } | null;
        button?: { label: string; url: string };
    };
};
